import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Agreement } from '../agreement/agreement';
import { Button } from '../../common/button/button';
import { IField } from '../../../interfaces/field.interface';
import { FieldIcon } from '../../../enums/field-icon.enum';
import { AGREEMENT_ERROR, INCORRECT_PHONE_ERROR } from '../../../constants';
import { PhoneFormData } from '../../../interfaces/phone-form-data.interface';
import styles from './phone-form.module.scss';
import { FormField } from '../form-field/form-field';
import { PopupWindowID } from '../../../enums/popup-window-id.enum';
import { device } from '../../../services/device-detector.service';
import { update } from '../../../redux/phone-form-slice';
import { displayPopupWindow } from '../../../redux/display-slice';
import { setActivePopupWidnowId } from '../../../redux/quiz-slice';
import { StylesContext } from '../../../contexts/styles-context/styles.context';
import { useCustomStylesOnHover } from '../../../hooks/use-custom-styles-on-hover.hook';
import cn from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux.hook';
import { useGetUserInfoQuery } from '../../../services/api.service';
import { useYandexId } from '../../../hooks/use-yandex-id';
import { updateState } from '../../../redux/service-slice';
import { FormFieldVariants } from '../form-field/form-field-variants';
import { ButtonBack } from '../../common/button-back/button-back';

interface PhoneFormProps {
  active: boolean
  button: string;
  fields?: IField[];
  phoneField?: {
    order?: number;
    label?: string;
    icon?: FieldIcon
  }
  privacyPolicyUrl: string;
  onSubmit: (values: PhoneFormData) => void;
  agreementValidationInPopup?: boolean;
  mode?: "phone-form" | "sign-up" | "phone-form-with-back-button";
  formViewType?: "default" | "grid" | "variants";
  getNode?: (node: HTMLElement) => void;
  backButton?: boolean;
  onBackButtonClick?: () => void;
  buttonBackDisabled?: boolean
}

const defaultPhoneField: IField = {
  name: "phone",
  label: "Ваш телефон",
  placeholder: " +7 (",
  type: "tel",
  autoComplete: "off",
  mask: '{+7} (000) 000-0000',
  required: true,
  validation: [
    {
      regExp: "(?:8|\\+7) \\([^78]\\d{2}\\) \\d{3}-\\d{4}",
      message: INCORRECT_PHONE_ERROR
    }
  ]
}

export const PhoneForm = (props: PhoneFormProps) => {
  const {
    button,
    fields,
    phoneField,
    privacyPolicyUrl,
    onSubmit,
    mode = "phone-form",
    agreementValidationInPopup = false,
    formViewType,
    getNode,
    backButton,
    onBackButtonClick,
    buttonBackDisabled
  } = props;
  const [formFields, setFormFields] = useState([...(fields || [])]);
  const [formPristine, setFormPristine] = useState(true);
  const [phoneFieldData] = useState({ ...defaultPhoneField, ...phoneField });
  const [error, setError] = useState<{ field?: keyof PhoneFormData, message?: string }>();
  const dispatch = useAppDispatch();
  const [hasFocus, setHasFocus] = useState(false);
  const phoneFormState = useAppSelector(state => state.phoneForm);
  const { styles: customStyles } = useContext(StylesContext);
  const { modifiedCustomStyles: {
    primaryButtons = {},
    minorButtons = {},
    allButtons = {},
    inputFields = {}
  }, onMouseEnter, onMouseOut } = useCustomStylesOnHover(customStyles);
  const { yandexIdAuthorized, yaClientIdButtonActive } = useAppSelector(state => state.service);

  const minWidth768 = useMediaQuery({ minWidth: 768 });

  const [yaClientIdAccessToken, setYaClientIdAccessToken] = useState("");
  const { data: yaUserInfo } = useGetUserInfoQuery(yaClientIdAccessToken, { skip: yaClientIdAccessToken == "" });
  const init = useYandexId();

  useEffect(() => {
    if (init && !yandexIdAuthorized && !yaClientIdButtonActive) {
      init(() => dispatch(updateState({ yaClientIdButtonActive: true })))
        .then((response: any) => {
          if (response?.access_token) {
            setYaClientIdAccessToken(response?.access_token);
            dispatch(updateState({ yandexIdAuthorized: true }));
          }
        });
    }
  }, [init, yandexIdAuthorized, yaClientIdButtonActive, dispatch]);

  useEffect(() => {
    if (yaUserInfo) {
      dispatch(update({
        phone: yaUserInfo.default_phone?.number || "",
        name: yaUserInfo.first_name || ""
      }));
      dispatch(updateState({ yaClientIdButtonActive: false }));
    }
  }, [yaUserInfo, dispatch]);

  useEffect(() => {
    if (!fields?.some(field => field.name == "phone")) {
      setFormFields(
        [...(fields || []), phoneFieldData].sort((a, b) => (a.order || 0) - (b.order || 0))
      );
    }
  }, [fields, phoneFieldData]);

  const validateField = useCallback((field: IField, value: any) => {
    if (field?.required) {
      const message = field.label ? "Пожалуйста, укажите " + field.label : "Пожалуйста, заполните поле";
      if (!value) {
        setError({ field: field.name, message });
        return false;
      }
    }

    if (field?.validation?.length) {
      for (let rule of field.validation) {
        const regExp = new RegExp(rule.regExp);
        if (!regExp.test(value)) {
          setError({ field: field.name, message: rule.message });
          return false;
        }
      }
    }

    setError({});
    return true;
  }, [])

  const validateAll = useCallback(() => {
    for (let field of formFields) {
      const value = phoneFormState[field.name] || "";
      if (!validateField(field, value)) return false;
    }

    if (!phoneFormState.agreement) {
      if (agreementValidationInPopup) {
        dispatch(setActivePopupWidnowId(PopupWindowID.AGREEMENT));
        dispatch(displayPopupWindow(true));
      } else setError({ field: "agreement", message: AGREEMENT_ERROR });

      return false;
    }

    setError({});
    return true;
  }, [formFields, phoneFormState, validateField, agreementValidationInPopup, dispatch]);

  const getPhoneFormStateWithDefaultValues = (formFields: IField[], phoneFormState: PhoneFormData & Record<string, any>) => {
    const state = { ...phoneFormState };
    formFields.forEach(field => {
      if (!state[field.name]) state[field.name] = field.defaultValue;
    });
    return state;
  };

  const submitHandler = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validateAll()) onSubmit && onSubmit(getPhoneFormStateWithDefaultValues(formFields, phoneFormState));
    setFormPristine(false);
  }, [validateAll, onSubmit, phoneFormState]);

  const onInputChange = useCallback((fieldName: string, value: any) => {
    dispatch(update({ [fieldName]: value }));
    const field = formFields.find(field => field.name == fieldName);
    if (!formPristine) validateField(field, value);
  }, [validateField, formFields, formPristine, dispatch]);

  const changeHandler = useCallback((element: HTMLInputElement & EventTarget | { name: string, value: any }) => {
    onInputChange(element.name, element.value)
  }, [onInputChange]);

  const agreementChangeHandler = useCallback((value: boolean) => {
    onInputChange("agreement", value);
  }, [onInputChange]);

  const pasteHanlder = useCallback((e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.currentTarget.name != "phone") return;

    const number = e.clipboardData.getData("Text");
    const formattedNumber = number.replace(/\+7|7|8/i, '');
    dispatch(update({ phone: formattedNumber }));
  }, [dispatch]);

  const focusHandler = useCallback(() => setHasFocus(true), []);
  const blurHandler = useCallback(() => setHasFocus(false), []);

  return (
    <form ref={getNode} onSubmit={submitHandler} className={cn(styles.phoneForm, styles["phoneForm_" + formViewType])}>
      <div id="yandex_id_container" className={cn({ active: yaClientIdButtonActive })}></div>
      {formFields.map((field, index) => (<>
        {field.type == "variants" ?
          <FormFieldVariants
            key={field.name || field.label}
            label={field.label}
            name={field.name}
            answers={field.answers}
            onChange={changeHandler}
            value={phoneFormState[field.name] ?? ""}
            defaultValue={field.defaultValue}
          /> :
          <FormField
            key={field.name || field.label}
            label={field.label}
            value={phoneFormState[field.name] ?? ""}
            name={field.name}
            placeholder={field.placeholder}
            type={field.type}
            mask={field.mask || undefined}
            icon={field.icon}
            autoComplete={device.isIOS() ? "password" : field.autoComplete}
            hasError={error?.field == field.name}
            animateCursor={!hasFocus && field.name == "phone"}
            onFocus={focusHandler}
            onBlur={blurHandler}
            doFocus={minWidth768 && ((index == 0 && field.required) || (field.name == "phone" && !formFields[0].required))}
            onPaste={pasteHanlder}
            onChange={changeHandler}
            options={field.options}
            formViewType={formViewType}
            customStyles={{
              ...inputFields,
              width: formViewType == "grid" && minWidth768 ? `calc(50% - 12px)` : undefined
            }}
          />
        }
        {error?.field == field.name && <div key={`error-for-${field.name}`} className={cn(styles.error, styles['error_' + formViewType])}>{error.message}</div>}
      </>))}
      <div className={styles.buttonContainer}>
        {backButton && <ButtonBack
          active={true}
          onClick={onBackButtonClick}
          mode='phone-form'
          disabled={buttonBackDisabled}
          customStyles={{ ...allButtons, ...minorButtons }}
        />}
        <Button
          onMouseOut={() => onMouseOut("primaryButtons")}
          onMouseEnter={() => onMouseEnter("primaryButtons")}
          customStyles={{
            ...allButtons, ...primaryButtons,
            minWidth: formViewType == "grid" && minWidth768 ? "372px" : "",
            width: formViewType == "grid" && minWidth768 ? "auto" : "",
            marginLeft: formViewType == "grid" && minWidth768 ? "0" : undefined
          }}
          type='submit'
          mode={formViewType == "variants" ? "sign-up-variants" : mode}
          value={button}
          disabled={Boolean(error?.message)}
        />
      </div>
      <Agreement
        privacyPolicyUrl={privacyPolicyUrl}
        checked={phoneFormState.agreement}
        onChange={agreementChangeHandler}
      />
      {error?.field == "agreement" && <div className={styles.error}>{error.message}</div>}
    </form>
  );
}